import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { environment } from 'src/environments/environment';
import { API } from 'aws-amplify';
import { Phone } from './core.module';
import { DEFAULT_GET_LIMIT } from '../shared/constants/operationals';
import { FormService } from './form.service';
import { FormGroup } from '@angular/forms';

export const SELLERS_API_NAME: string = 'sellers';
export const SELLERS_API_ENDPOINT: string = environment.API_BASE_PATH + '/seller';

@Injectable({
  providedIn: 'root',
})
export class SellersService {
  constructor() {}

  listSellers(list_params?: ListSellersParams): Observable<any> {
    const api_name: string = SELLERS_API_NAME;
    const path: string = '';

    let query_string_params: any = {
      cursor: list_params?.cursor || null,
      limit: list_params?.limit || DEFAULT_GET_LIMIT,
    };
    if (list_params?.seller_id) query_string_params['seller_id'] = list_params.seller_id;
    if (list_params?.seller_name) query_string_params['seller_name'] = list_params.seller_name;
    if (list_params?.phone_value) query_string_params['phone_value'] = list_params.phone_value;
    if (list_params?.email) query_string_params['email'] = list_params.email;
    if (list_params?.is_confirmed) query_string_params['is_confirmed'] = list_params.is_confirmed;

    const init = { response: true, queryStringParameters: query_string_params };
    return from(API.get(api_name, path, init));
  }

  readSummary(): Observable<any> {
    const api_name: string = SELLERS_API_NAME;
    const path: string = '/summary';
    const init = { response: true };
    return from(API.get(api_name, path, init));
  }

  readSeller(seller_id: string): Observable<any> {
    const api_name: string = SELLERS_API_NAME;
    const path: string = `/${seller_id}`;
    const init = { response: true };
    return from(API.get(api_name, path, init));
  }

  edit(seller: Seller): Observable<any> {
    const api_name: string = SELLERS_API_NAME;
    const path: string = `/${seller.seller_id}`;
    const init = { response: true, body: seller };
    return from(API.put(api_name, path, init));
  }

  setPhone(seller: Seller): Observable<any> {
    const api_name: string = SELLERS_API_NAME;
    const path: string = `/${seller.seller_id}/phone`;
    const init = { response: true, body: seller };
    return from(API.put(api_name, path, init));
  }

  setMfa(seller_id: string, type: SellerMfaType): Observable<any> {
    const api_name: string = SELLERS_API_NAME;
    const path: string = `/${seller_id}/mfa`;
    const init = { response: true, body: { type } };
    return from(API.put(api_name, path, init));
  }

  unblock(seller: Seller): Observable<any> {
    const api_name: string = SELLERS_API_NAME;
    const path: string = `/${seller.seller_id}/unblock`;
    const init = { response: true, body: seller };
    return from(API.post(api_name, path, init));
  }

  confirmByAdmin(seller_id: string, confirm_reason: string): Observable<any> {
    const api_name: string = SELLERS_API_NAME;
    const path: string = `/${seller_id}/confirm-otp`;
    const init = {
      response: true,
      body: { confirm_reason },
    };
    return from(API.post(api_name, path, init));
  }
}

export interface ListSellersParams {
  seller_id?: string;
  seller_name?: string;
  phone_value?: string;
  email?: string;
  is_confirmed?: string;

  cursor?: string | null;
  limit?: number;
}

export interface Seller {
  seller_id?: string;
  object?: string;
  seller_sub?: string;
  firstname: string;
  lastname: string;
  phone: Phone;
  email: string;
  password_set_at?: number;
  is_confirmed?: boolean;
  confirmed_by?: string;
  confirmed_by_ext_id?: string;
  confirm_reason?: string;
  is_profile_completed?: boolean;
  preferred_mfa?: string;
  blocked_at?: number;
  blocked_reason?: string;
  created_at?: number;
  last_login_at?: number;
  updated_at?: number;
  company_id?: string;
  company_business_name?: string;
  seller_invitation_id?: string;
  last_sign_in_attempt_at?: number;
  unblocked_at?: number;
  unblocked_by?: string;
  current_login_attempts?: string;
}

export enum SellerRole {
  owner = 'owner',
  member = 'member',
}

export interface SellerSummary {
  length: number;
  onboarding_completed: number;
  confirmed: number;
  not_confirmed: number;
}

export enum SellerConfirmedSemaphore {
  green = 'green',
  orange = 'orange',
  red = 'red',
}

export enum SellerMfaType {
  sms = 'sms',
  totp = 'totp',
}
